import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Draggable } from 'react-beautiful-dnd'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Chip, Typography, Stack, Tooltip, IconButton } from '@mui/material_modern'
import { Autorenew } from '@mui/icons-material'
import CommentIcon from '@mui/icons-material/Comment'
import ImageIcon from '@mui/icons-material/Image'
import { showFormModal } from 'state/ui'
import { DISPATCH_TICKET_TYPES, DT_STATUS_ENUM } from 'utils/constants'
import FORM_MODALS from 'utils/ui/modals'
import { dateFormatter, dateTimeFormatter, timeFormatter } from 'utils/data-manipulation/valueFormatter'
import { capAllWords, determineDateContextIcon } from 'utils/data-manipulation/common'
import {
  DISPATCH_STATUS_ICONS_DICT,
  DRAG_DROP_BACKGROUND_COLOR,
} from 'components/containers/load-board/utils/constants'
import { useTheme } from '@mui/styles'

const DispatchActiveTicketsLoadBoardItem = ({ item, index }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const { inOrgMode } = useSelector(state => state.ui)
  const { showPastLoadBoard } = useSelector(state => state.settings.settings.dispatchSettings.loadBoard)

  // This is where we track the scheduled date for icon purposes
  const scheduleDateAlertComponent = useMemo(() => {
    return determineDateContextIcon(item.scheduleDate, item.status)
  }, [item])

  // Extract container names and types using filter and map
  const containerNames = (item.dispatchTicketAssets || [])
    .filter(asset => asset.asset.assetCategory === 'CONTAINER')
    .map(asset => `${asset.asset.assetName} (${asset.asset.type})`)

  // Determine containerNameText
  const containerNameText = containerNames.length > 1 ? 'Multiple' : containerNames[0] || 'None'

  // Prepare tooltip content
  const containerTooltipContent = (
    <div>
      {containerNames.map((name, index) => (
        <div key={index}>{name}</div>
      ))}
    </div>
  )

  const locationAddressText = `${item.location?.address1} ${item.location?.address2}` || ''

  const locationAddressTooltipText =
    `${item.location?.address1} ${item.location?.address2}, ${item.location?.city}, ${item.location?.state} ${item.location?.zip}` ||
    ''

  return (
    <Draggable key={item.id} draggableId={item.id} index={index}>
      {/* {(provided, snapshot) => ( */}
      {provided => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          sx={{
            userSelect: 'none',
            padding: '4px 8px 4px 8px',
            margin: '0 0 8px 0',
            borderRadius: '4px',
            backgroundColor: DRAG_DROP_BACKGROUND_COLOR[item.status],
            boxShadow: '0px 1px rgba(0, 0, 0, 0.2)',
            ...provided.draggableProps.style,
          }}
          onClick={() =>
            dispatch(
              showFormModal({
                formModalName: FORM_MODALS.DISPATCH_TICKET_PREVIEW,
                formModalProps: {
                  ticketDataFromList: item,
                },
              }),
            )
          }
        >
          <Stack height="100%" direction="column" justifyContent="space-between">
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography
                  variant="overline"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: 'bold',
                    color: 'text.primary',
                  }}
                >
                  {`#${item.externalId}`}
                </Typography>
                {item?.recurringDispatchTicketId && (
                  <Tooltip
                    title="Recurring Dispatch Ticket"
                    placement="top"
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -10],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <Autorenew
                      sx={{
                        fontSize: 18,
                        color: theme.palette.text.hint,
                        pb: '3px',
                      }}
                    />
                  </Tooltip>
                )}
              </Stack>
              {inOrgMode && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  maxWidth="75%"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  <Tooltip
                    title={`${capAllWords(item.interYard?.startLocationName || item.yard?.name) || ''}${item.interYard?.endLocationName ? ` → ${capAllWords(item.interYard.endLocationName)}` : ''}`}
                    placement="right"
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {`${capAllWords(item.interYard?.startLocationName || item.yard?.name) || ''}`}
                    </Typography>
                    {item.interYard?.endLocationName && (
                      <Typography variant="caption">{` → ${capAllWords(item.interYard.endLocationName)}`}</Typography>
                    )}
                  </Tooltip>
                </Stack>
              )}
            </Stack>
            <Tooltip title={`${item.customer?.displayName} - ${locationAddressTooltipText}`} placement="left">
              <Typography
                variant="body1"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {`${item.customer?.displayName} - ${locationAddressText}`}
              </Typography>
            </Tooltip>
            {containerNameText !== 'None' && (
              <Tooltip title={containerTooltipContent} placement="left">
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {`${containerNameText}`}
                </Typography>
              </Tooltip>
            )}
            <Stack direction="row" alignItems="center" justifyContent="space-between" marginTop="4px">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {showPastLoadBoard && item.status !== DT_STATUS_ENUM.REQUESTED && (
                  <Stack direction="row" alignItems="center" justifyContent="space-around">
                    {scheduleDateAlertComponent}
                    <Typography
                      variant="caption"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {`${dateFormatter(item.scheduleDate) || ''}`}
                    </Typography>
                  </Stack>
                )}
                {item?.scheduleTimeActive && item.status !== DT_STATUS_ENUM.REQUESTED ? (
                  <Typography variant="caption">{timeFormatter(new Date(item.scheduleDate))}</Typography>
                ) : null}
                {item.status === DT_STATUS_ENUM.REQUESTED && (
                  <Typography variant="caption">
                    {item.requestedScheduleDate ? (
                      dateTimeFormatter(item.requestedScheduleDate)
                    ) : (
                      <i>As Soon As Possible</i>
                    )}
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {DISPATCH_STATUS_ICONS_DICT[item.status]?.loadBoard || null}
                <Typography variant="caption">{DISPATCH_TICKET_TYPES[item.type]?.name}</Typography>
              </Stack>
            </Stack>
            {item.images?.length > 0 ||
            item.dispatchTicketAssets[0]?.completionNote ||
            item.requestNotes ||
            item.note ? (
              <Stack direction="row" display="flex" alignItems="center" justifyContent="start" marginTop="4px">
                {item.dispatchTicketAssets[0]?.completionNote && (
                  <Tooltip title={`Driver Note: ${item.dispatchTicketAssets[0].completionNote}`} placement="left">
                    <Chip
                      icon={<CommentIcon sx={{ fontSize: '0.2rem' }} />}
                      label={item.dispatchTicketAssets[0].completionNote}
                      size="small"
                      variant="filled"
                      sx={{
                        width: '85%',
                        flex: '11',
                        justifyContent: 'start',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </Tooltip>
                )}
                {!item.dispatchTicketAssets[0]?.completionNote && item.note && (
                  <Tooltip title={`Note: ${item.note}`} placement="left">
                    <Chip
                      icon={<CommentIcon sx={{ fontSize: '0.2rem' }} />}
                      label={item.note}
                      size="small"
                      variant="filled"
                      sx={{
                        width: '85%',
                        flex: '11',
                        justifyContent: 'start',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </Tooltip>
                )}
                {!item.dispatchTicketAssets[0]?.completionNote && !item.note && item.requestNotes && (
                  <Tooltip title={`Request Note: ${item.requestNotes}`} placement="left">
                    <Chip
                      icon={<CommentIcon sx={{ fontSize: '0.2rem' }} />}
                      label={item.requestNotes}
                      size="small"
                      variant="filled"
                      sx={{
                        width: '85%',
                        flex: '11',
                        justifyContent: 'start',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </Tooltip>
                )}
                {item.images && item.images.length > 0 && (
                  <Tooltip title="View Images">
                    <IconButton
                      size="small"
                      sx={{
                        width: '34px',
                      }}
                      onClick={e => {
                        e.stopPropagation()
                        dispatch(
                          showFormModal({
                            formModalName: FORM_MODALS.DISPATCH_TICKET_IMAGES_BLADE,
                            formModalProps: {
                              dispatchTicketData: item,
                            },
                          }),
                        )
                      }}
                    >
                      <ImageIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            ) : null}
          </Stack>
        </Box>
      )}
    </Draggable>
  )
}

DispatchActiveTicketsLoadBoardItem.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
}

DispatchActiveTicketsLoadBoardItem.defaultProps = {}

export default DispatchActiveTicketsLoadBoardItem
